"use client";

/* eslint-disable react/no-array-index-key */

import { cva } from "class-variance-authority";
import { useCallback, useEffect, useState } from "react";
import { Tabs, Tab, TabList, TabPanel } from "ui-components";

import {
  BlockedUserResponse,
  ConnectionsDtoStatus,
  useUserGetBlockedUsers,
  useUserGetMyConnectionsCounts,
} from "@/src/hooks/api/generated";

import { BlockedMembersListing } from "./BlockedMembersListing";
import { ConnectionsListing } from "./ConnectionsListing";
import { SuggestedConnections } from "./SuggestedConnections";

enum tabKeys {
  requested = "connection-requested",
  exists = "connection-exists",
  blocked = "blocked-members",
}

export default function MyConnections(): JSX.Element {
  const [blockUsersData, setBlockUsersData] = useState<BlockedUserResponse>();
  const { data: connectionsCountData, refetch: refetchConnectionCount } =
    useUserGetMyConnectionsCounts();
  const [activeTab, setActiveTab] = useState<tabKeys>(tabKeys.requested);

  const { mutate: fetchBlockedUsers, status: blockUsersFetchingStatus } =
    useUserGetBlockedUsers();

  const getData = useCallback(() => {
    fetchBlockedUsers(
      {
        data: {},
      },
      {
        onSuccess: (data) => {
          setBlockUsersData(data);
        },
      }
    );
  }, [fetchBlockedUsers, setBlockUsersData]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <div className="w-full flex flex-col gap-y-20 mb-20">
      <div className="w-full flex flex-col lg:flex-row justify-between">
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(key as tabKeys)}
        >
          <TabList className="flex items-center gap-x-0">
            <Tab
              className={tabClasses()}
              tabKey={tabKeys.requested}
              tabName={`Connection Requests · ${
                (connectionsCountData?.pendingConnections || 0) +
                (connectionsCountData?.requestedConnections || 0)
              }`}
            />
            <Tab
              tabKey={tabKeys.exists}
              className={tabClasses()}
              tabName={`My Connection . ${
                connectionsCountData?.activeConnections || 0
              }`}
            />
            <Tab
              tabKey={tabKeys.blocked}
              className={tabClasses()}
              tabName={`Blocked Members . ${
                connectionsCountData?.blockedUsers || 0
              }`}
            />
          </TabList>

          <TabPanel className="px-0" tabKey={tabKeys.requested}>
            <div className="max-w-full lg:w-[759px] flex flex-col gap-y-5 !p-0 w-full">
              <ConnectionsListing
                title="Invitations to connect"
                status={ConnectionsDtoStatus.pending}
                twoColumnLayout
                hideThreeDotsLayout
                refetchConnectionCount={refetchConnectionCount}
              />
              <ConnectionsListing
                title="Pending Connections"
                status={ConnectionsDtoStatus.request}
                twoColumnLayout
                refetchConnectionCount={refetchConnectionCount}
              />
            </div>
          </TabPanel>
          <TabPanel className="px-0" tabKey={tabKeys.exists}>
            <div className="max-w-full lg:w-[759px] flex flex-col gap-y-5 ">
              <ConnectionsListing
                title="My Connections"
                status={ConnectionsDtoStatus.active}
                onUserBlocked={() => {
                  getData();
                  refetchConnectionCount();
                }}
                twoColumnLayout
              />
            </div>
          </TabPanel>
          <TabPanel className="px-0" tabKey={tabKeys.blocked}>
            <div className="w-full lg:w-[759px] flex flex-col gap-y-5">
              <BlockedMembersListing
                blockUsersFetchingStatus={blockUsersFetchingStatus}
                blockUsersData={blockUsersData}
                refetchBlockUsers={() => {
                  getData();
                  refetchConnectionCount();
                }}
              />
            </div>
          </TabPanel>
        </Tabs>

        {/* People you might be interested in */}
        <div className="w-full lg:max-w-[366px]">
          <SuggestedConnections
            title="People you might be interested in"
            hasCtaLink
          />
        </div>
      </div>
    </div>
  );
}

const tabClasses = cva(
  "flex flex-1 pb-2 justify-center items-center border-b-2 !border-brand-light-gray  data-[selected]:border-b-2  data-[selected]:!border-brand-primary"
);
